import { apiDelete, apiGet, apiPost } from '../../../adapters/api-client/api';

/** `KID` while the learner's own space is open on this session (GAP-S01-01). */
export type ContextMode = 'KID' | 'PARENT';

export interface ActiveContext {
  role: string;
  actingAs: string;
  /** §VI.1: alias of the parent account until a household entity exists. */
  householdId?: string;
  studentId: string;
  studentName: string;
  /** Absent from servers that predate kid mode, which read as the parent's own. */
  mode?: ContextMode;
}

export interface SwitchStudentResponse {
  activeContext: ActiveContext;
}

/** `activeContext: null` is the parent's own view ("Me", PRT-BR-P02-09). */
export interface CurrentContextResponse {
  activeContext: ActiveContext | null;
  /**
   * Whether this device is locked in the child's space. Can be true with no
   * learner, when the learner was removed while kid mode was open: the lock
   * holds until the Parent re-authenticates.
   */
  kidMode?: boolean;
}

/** The Parent session the identity provider has just re-established. */
export interface ReauthenticatedSession {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

/** The lock lifted, with the fresh session when there was a lock to lift. */
export type KidExitResult = CurrentContextResponse & { session?: ReauthenticatedSession };

/** The provider asked for the second factor; `session` goes back unchanged. */
export interface KidExitChallenge {
  challenge: 'SOFTWARE_TOKEN_MFA';
  session: string;
}

/** The codes the kid-mode endpoints answer with, read from `ApiError.body.code`. */
export const KID_MODE_CODES = {
  active: 'KID_MODE_ACTIVE',
  reauthFailed: 'KID_REAUTH_FAILED',
  reauthUnavailable: 'KID_REAUTH_UNAVAILABLE',
  consentRequired: 'KID_CONSENT_REQUIRED',
  otherLearner: 'KID_MODE_OTHER_LEARNER',
} as const;

/**
 * Switch the authenticated parent's active student context.
 * POST /api/v1/context/switch-student
 */
export function switchStudentContext(studentId: string): Promise<SwitchStudentResponse> {
  return apiPost<SwitchStudentResponse>('/api/v1/context/switch-student', { studentId });
}

/**
 * The context the server holds, revalidated against current ownership.
 * GET /api/v1/context/active
 */
export function getActiveContext(): Promise<CurrentContextResponse> {
  return apiGet<CurrentContextResponse>('/api/v1/context/active');
}

/**
 * Return to the parent view on the server, so a refresh cannot restore the learner.
 * DELETE /api/v1/context/active
 */
export function clearActiveContext(): Promise<CurrentContextResponse> {
  return apiDelete<CurrentContextResponse>('/api/v1/context/active');
}

/** Open the learner's space on this session. POST /api/v1/context/kid-mode/enter */
export function enterKidModeRequest(studentId: string): Promise<CurrentContextResponse> {
  return apiPost<CurrentContextResponse>('/api/v1/context/kid-mode/enter', { studentId });
}

/**
 * DEC-S2-S01-04 — leave the learner's space: the Parent's password, to the
 * identity provider. POST /api/v1/context/kid-mode/exit
 */
export function exitKidModeRequest(password: string): Promise<KidExitResult | KidExitChallenge> {
  return apiPost<KidExitResult | KidExitChallenge>('/api/v1/context/kid-mode/exit', { password });
}

/** The authenticator code, for an account with TOTP. POST /api/v1/context/kid-mode/exit/mfa */
export function exitKidModeMfaRequest(session: string, code: string): Promise<KidExitResult> {
  return apiPost<KidExitResult>('/api/v1/context/kid-mode/exit/mfa', { session, code });
}
